import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getLogoPath } from '../../utils/fluxTheme';
import './LoginPage.css';

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' → 'code' → 'success'
  const [email, setEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const fluxLogo = getLogoPath();

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  // ── STEP 1: Request reset code ─────────────────────────────────────────────
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || email.length > 254) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/forgot-password', { email });

      if (response.data.success) {
        setMaskedPhone(response.data.maskedPhone || '');
        setStep('code');
        setResendCountdown(60);
      }
    } catch (err) {
      console.error('[FORGOT PASSWORD] Error:', err);
      // Generic message — don't reveal whether email exists
      setError(err.response?.data?.error || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Resend code ────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendCountdown > 0 || loading) return;
    setError('');
    setLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { email });
      setResendCountdown(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // ── STEP 2: Verify code + set new password ─────────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!resetCode || resetCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    if (newPassword.length < 8 || newPassword.length > 128) {
      setError('Password must be 8–128 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/auth/reset-password', {
        email,
        code: resetCode,
        newPassword
      });

      if (response.data.success) {
        setStep('success');
        setTimeout(() => navigate('/agent/login'), 3000);
      }
    } catch (err) {
      console.error('[RESET PASSWORD] Error:', err);
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Renders ────────────────────────────────────────────────────────────────
  const renderEmailStep = () => (
    <form onSubmit={handleRequestReset} className="login-form">
      <div className="form-group">
        <label>Email Address</label>
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          placeholder="Enter your registered email"
          disabled={loading}
          maxLength="254"
          autoFocus
          required
        />
        <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '5px', display: 'block' }}>
          📱 We'll send a verification code to your registered phone number
        </small>
      </div>

      {error && <div className="error-message">{error}</div>}

      <button type="submit" className="login-button" disabled={loading}>
        {loading ? 'Sending Code…' : 'Send Reset Code'}
      </button>

      <div style={{ textAlign: 'center', marginTop: '15px' }}>
        <Link to="/agent/login" style={{ color: '#6c757d', fontSize: '14px', textDecoration: 'none' }}>
          ← Back to Login
        </Link>
      </div>
    </form>
  );

  const renderCodeStep = () => (
    <form onSubmit={handleResetPassword} className="login-form">
      {/* OTP Code */}
      <div className="form-group">
        <label>Verification Code</label>
        {maskedPhone && (
          <p style={{ color: '#6c757d', fontSize: '13px', marginBottom: '8px' }}>
            Code sent to <strong>{maskedPhone}</strong>
          </p>
        )}
        <input
          type="text"
          value={resetCode}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
            setResetCode(v);
            setError('');
          }}
          placeholder="000000"
          className="code-input"
          maxLength="6"
          disabled={loading}
          autoFocus
          required
        />
        <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
          Enter the 6-digit SMS code
        </small>
      </div>

      {/* New Password */}
      <div className="form-group">
        <label>New Password</label>
        <div style={{ position: 'relative' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
            placeholder="New password (8+ characters)"
            disabled={loading}
            minLength="8"
            maxLength="128"
            required
            style={{ paddingRight: '40px' }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            style={{
              position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', fontSize: '16px'
            }}
          >
            {showPassword ? '🙈' : '👁️'}
          </button>
        </div>
      </div>

      {/* Confirm Password */}
      <div className="form-group">
        <label>Confirm New Password</label>
        <input
          type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
          placeholder="Confirm new password"
          disabled={loading}
          minLength="8"
          maxLength="128"
          required
        />
        {confirmPassword && newPassword !== confirmPassword && (
          <small style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            Passwords don't match
          </small>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <button
        type="submit"
        className="login-button"
        disabled={loading || resetCode.length !== 6 || newPassword !== confirmPassword}
      >
        {loading ? 'Resetting Password…' : 'Reset Password'}
      </button>

      {/* Resend */}
      <div style={{ textAlign: 'center', marginTop: '12px' }}>
        {resendCountdown > 0 ? (
          <span style={{ color: '#6c757d', fontSize: '13px' }}>
            Resend code in {resendCountdown}s
          </span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={loading}
            style={{
              background: 'transparent', border: 'none', color: '#007bff',
              cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px',
              textDecoration: 'underline', padding: '4px'
            }}
          >
            Didn't receive it? Resend code
          </button>
        )}
      </div>

      <div style={{ textAlign: 'center', marginTop: '8px' }}>
        <button
          type="button"
          onClick={() => { setStep('email'); setResetCode(''); setError(''); }}
          style={{
            background: 'transparent', border: 'none', color: '#6c757d',
            cursor: 'pointer', fontSize: '13px', textDecoration: 'underline', padding: '4px'
          }}
        >
          ← Try a different email
        </button>
      </div>
    </form>
  );

  const renderSuccessStep = () => (
    <div className="login-form" style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '52px', marginBottom: '16px' }}>✅</div>
      <h2 style={{ color: '#28a745', marginBottom: '12px' }}>Password Reset!</h2>
      <p style={{ fontSize: '15px', color: '#6c757d', marginBottom: '8px' }}>
        Your password has been updated successfully.
      </p>
      <p style={{ fontSize: '13px', color: '#adb5bd' }}>Redirecting to login…</p>
      <div style={{
        margin: '24px auto 0', width: '36px', height: '36px',
        border: '4px solid #f3f3f3', borderTop: '4px solid #28a745',
        borderRadius: '50%', animation: 'spin 1s linear infinite'
      }} />
      <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
    </div>
  );

  // ── Layout ─────────────────────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-container">
        {fluxLogo && (
          <div className="login-logo">
            <img src={fluxLogo} alt="Flux Protocol" className="flux-logo" />
          </div>
        )}

        <div className="login-header">
          <h1>
            {step === 'email' && 'Forgot Password?'}
            {step === 'code' && 'Reset Password'}
            {step === 'success' && 'All Done!'}
          </h1>
          <p>
            {step === 'email' && 'Enter your email to receive a reset code'}
            {step === 'code' && 'Enter the code and your new password'}
            {step === 'success' && 'Password changed successfully'}
          </p>
        </div>

        {/* Step indicator */}
        {step !== 'success' && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '24px' }}>
            {['email', 'code'].map((s, i) => (
              <div key={s} style={{
                width: '32px', height: '4px', borderRadius: '2px',
                background: step === s || (step === 'code' && i === 0) ? '#667eea' : '#dee2e6',
                transition: 'background 0.3s'
              }} />
            ))}
          </div>
        )}

        {step === 'email' && renderEmailStep()}
        {step === 'code' && renderCodeStep()}
        {step === 'success' && renderSuccessStep()}
      </div>
    </div>
  );
}

export default ForgotPassword;
